import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { useMantineColorScheme, Text, Button } from '@mantine/core';

/* WeeklyStackedSingleRangeSelector
   --------------------------------------------------
   Single continuous weekly range across 7 stacked daily tracks (15-min granularity).
   Visual style mirrors DailyTimeRangeSelector rows.
   User can drag start in one day and extend into later days (or wrap backwards for crossing week boundary).

   Data model:
     weeklyRange = { startDay, startMinute, endDay, endMinute, wrap }
       - startMinute/endMinute are minutes from 0..1440 per day (endMinute exclusive)
       - wrap true if selection crosses end of Saturday into Sunday (endIdx < startIdx in linear index space)

   Granularity: 15 minutes.
   End-minute exclusive; display shows inclusive end minute - 1 (23:59 for midnight end).

   Props:
     initialRange: optional stored weeklyRange
     onRangeChange(range): callback after commit
*/

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const TOTAL_MINUTES_DAY = 1440; // 24*60
const SLOT = 15; // minutes per slot
const SLOTS_PER_DAY = TOTAL_MINUTES_DAY / SLOT; // 96
const TOTAL_WEEK_SLOTS = SLOTS_PER_DAY * 7; // 672
const TRACK_HEIGHT = 30;
const ROW_GAP = 0; // removed vertical gap for matrix layout (no spacing between weekday rows)
// Extra top margin to fit hour labels row
const MARGIN = { top: 26, right: 12, bottom: 10, left: 64 };

function fmtMinute(m) {
  if (m == null) return '--:--';
  if (m === TOTAL_MINUTES_DAY) return '24:00'; // boundary end of day
  const h = Math.floor(m / 60); const mm = m % 60;
  return `${String(h).padStart(2,'0')}:${String(mm).padStart(2,'0')}`;
}

// Compute selection object from drag state
function computeRange(start, current) {
  if (!start || !current) return null;
  const startIdx = start.day * SLOTS_PER_DAY + start.slot;
  const curIdx = current.day * SLOTS_PER_DAY + current.slot;
  // Single slot
  if (startIdx === curIdx) {
    return {
      startDay: start.day,
      startMinute: start.slot * SLOT,
      endDay: start.day,
      endMinute: start.slot * SLOT + SLOT,
      wrap: false,
    };
  }
  if (curIdx > startIdx) {
    const endDay = Math.floor(curIdx / SLOTS_PER_DAY);
    const endSlot = curIdx % SLOTS_PER_DAY;
    return {
      startDay: start.day,
      startMinute: start.slot * SLOT,
      endDay,
      endMinute: endSlot * SLOT + SLOT,
      wrap: false,
    };
  }
  // wrap around week
  const endDayWrap = Math.floor(curIdx / SLOTS_PER_DAY);
  const endSlotWrap = curIdx % SLOTS_PER_DAY;
  return {
    startDay: start.day,
    startMinute: start.slot * SLOT,
    endDay: endDayWrap,
    endMinute: endSlotWrap * SLOT + SLOT,
    wrap: true,
  };
}

function formatRangeDisplay(range) {
  if (!range) return 'No range selected';
  const start = `${DAYS[range.startDay]} ${fmtMinute(range.startMinute)}`;
  const end = `${DAYS[range.endDay]} ${fmtMinute(range.endMinute)}`; // boundary end
  // Duration calculation (exclusive end)
  const startIdx = range.startDay * TOTAL_MINUTES_DAY + range.startMinute;
  const endIdxExclusive = range.endDay * TOTAL_MINUTES_DAY + range.endMinute; // exclusive
  if (!range.wrap) {
    const dur = endIdxExclusive - startIdx;
    return `${start} → ${end}${range.wrap ? ' (wrap)' : ''} (${Math.floor(dur/60)}h ${dur%60}m)`;
  }
  // wrap: selection spans start..weekEnd + weekStart..end
  const weekEndIdxExclusive = 7 * TOTAL_MINUTES_DAY; // exclusive
  const durWrap = (weekEndIdxExclusive - startIdx) + endIdxExclusive;
  return `${start} → ${end} (wrap) (${Math.floor(durWrap/60)}h ${durWrap%60}m)`;
}

const WeeklyStackedSingleRangeSelector = ({ initialRange = null, onRangeChange }) => {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const overlayRef = useRef(null);
  const tooltipRef = useRef(null);
  const [width, setWidth] = useState(1100);

  const [range, setRange] = useState(initialRange);
  const [dragStart, setDragStart] = useState(null); // {day,slot}
  const [dragCurrent, setDragCurrent] = useState(null);
  const isDragging = !!dragStart && !!dragCurrent;

  // Sync internal range when parent resets (e.g., Delete All sets initialRange null)
  useEffect(() => {
    setRange(initialRange);
  }, [initialRange]);

  // Resize observer
  useEffect(() => {
    const el = containerRef.current; if (!el) return;
    const measure = () => { const w = el.getBoundingClientRect().width; if (w && Math.abs(w - width) > 8) setWidth(w); };
    measure(); let ro; if ('ResizeObserver' in window) { ro = new ResizeObserver(measure); ro.observe(el); }
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [width]);

  const clampSlot = (minutes) => {
    let m = Math.max(0, Math.min(TOTAL_MINUTES_DAY, minutes));
    if (m === TOTAL_MINUTES_DAY) return SLOTS_PER_DAY; // slot index beyond last for end
    return Math.floor(m / SLOT);
  };

  // Position helpers
  const getDayFromY = (y) => {
    // With ROW_GAP = 0 days are contiguous blocks of TRACK_HEIGHT
    const day = Math.floor(y / TRACK_HEIGHT);
    return Math.max(0, Math.min(6, day));
  };

  const buildScales = () => {
    const innerWidth = width - MARGIN.left - MARGIN.right;
    return d3.scaleLinear().domain([0, TOTAL_MINUTES_DAY]).range([0, innerWidth]);
  };

  const pointerToSlot = (clientX, clientY) => {
    const svg = svgRef.current; if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const xRel = clientX - rect.left - MARGIN.left;
    const yRel = clientY - rect.top - MARGIN.top;
    const xScale = buildScales();
    const minutes = Math.max(0, Math.min(TOTAL_MINUTES_DAY, xScale.invert(xRel)));
    const slot = clampSlot(minutes);
    const day = getDayFromY(yRel);
    return { day, slot: Math.min(slot, SLOTS_PER_DAY - 1) }; // cap inside
  };

  // Hover logic: if hovering inside existing selection show Range tooltip, else Slot tooltip
  const handleHoverMove = (e) => {
    if (dragStart) return; // dragging state manages its own tooltip
    const pos = pointerToSlot(e.clientX, e.clientY); if (!pos) return;
    const t = tooltipRef.current; if (!t) return;
    const rect = containerRef.current.getBoundingClientRect();
    t.style.display = 'block';
    t.style.left = (e.clientX - rect.left + 12) + 'px';
    t.style.top = (e.clientY - rect.top + 14) + 'px';

    const startM = pos.slot * SLOT;
    const endM = Math.min(startM + SLOT, TOTAL_MINUTES_DAY);

    let inside = false; let duration = null; let startTxt, endTxt, wrapFlag;
    if (range) {
      const absMinute = pos.day * TOTAL_MINUTES_DAY + startM;
      const startIdx = range.startDay * TOTAL_MINUTES_DAY + range.startMinute;
      const endIdxEx = range.endDay * TOTAL_MINUTES_DAY + range.endMinute; // exclusive
      if (!range.wrap) {
        inside = absMinute >= startIdx && absMinute < endIdxEx;
        duration = endIdxEx - startIdx;
      } else {
        // wrap covers startIdx..weekEnd + weekStart..endIdxEx
        const weekEndExclusive = 7 * TOTAL_MINUTES_DAY;
        inside = (absMinute >= startIdx) || (absMinute < endIdxEx);
        duration = (weekEndExclusive - startIdx) + endIdxEx;
      }
      if (inside) {
        startTxt = `${DAYS[range.startDay]} ${fmtMinute(range.startMinute)}`;
        endTxt = `${DAYS[range.endDay]} ${fmtMinute(range.endMinute)}`;
        wrapFlag = range.wrap;
      }
    }

    if (inside) {
      const durStr = `${Math.floor(duration/60)}h ${duration%60}m`;
      t.dataset.mode = 'range-hover';
      t.innerHTML = `<span style="font-weight:600;color:${dark?'#8bb4ff':'#1e4fa8'}">Range</span> ${startTxt} → ${endTxt}${wrapFlag?' (wrap)':''} · ${durStr}`;
      t.style.background = dark ? 'rgba(30,38,50,0.92)' : 'rgba(235,243,255,0.95)';
      t.style.borderColor = dark ? '#35507b' : '#95b8eb';
    } else {
      t.dataset.mode = 'slot-hover';
      t.innerHTML = `<span style="font-weight:600;color:${dark?'#f2c179':'#b45309'}">Slot</span> ${DAYS[pos.day]} ${fmtMinute(startM)} → ${fmtMinute(endM)}`;
      t.style.background = dark ? 'rgba(50,55,62,0.9)' : 'rgba(255,255,255,0.95)';
      t.style.borderColor = dark ? '#4b5663' : '#d5dae0';
    }
  };

  const handlePointerDown = (e) => {
    const pos = pointerToSlot(e.clientX, e.clientY); if (!pos) return;
    setDragStart(pos); setDragCurrent(pos);
  };
  const handlePointerMove = (e) => { if (!dragStart) return; const pos = pointerToSlot(e.clientX, e.clientY); if (!pos) return; setDragCurrent(pos); positionTooltip(e); };
  const handlePointerUp = () => { if (dragStart && dragCurrent) { const r = computeRange(dragStart, dragCurrent); setRange(r); onRangeChange?.(r); } setDragStart(null); setDragCurrent(null); hideTooltip(); };

  // Tooltip
  const hideTooltip = () => { const t = tooltipRef.current; if (t) t.style.display = 'none'; };
  const positionTooltip = (e) => {
    const t = tooltipRef.current; if (!t) return;
    const rect = containerRef.current.getBoundingClientRect();
    t.style.display = 'block';
    t.style.left = (e.clientX - rect.left + 12) + 'px';
    t.style.top = (e.clientY - rect.top + 14) + 'px';
    const draft = computeRange(dragStart, dragCurrent) || range;
    if (draft) {
      const startTxt = `${DAYS[draft.startDay]} ${fmtMinute(draft.startMinute)}`;
      const endTxt = `${DAYS[draft.endDay]} ${fmtMinute(draft.endMinute)}`;
      const durIdxStart = draft.startDay * TOTAL_MINUTES_DAY + draft.startMinute;
      const durIdxEnd = draft.endDay * TOTAL_MINUTES_DAY + draft.endMinute; // exclusive
      const duration = !draft.wrap ? (durIdxEnd - durIdxStart) : ((7 * TOTAL_MINUTES_DAY - durIdxStart) + durIdxEnd);
      const durStr = `${Math.floor(duration/60)}h ${duration%60}m`;
      t.dataset.mode = 'drag';
      t.innerHTML = `<span style="font-weight:600;color:${dark?'#8bb4ff':'#1e4fa8'}">Range</span> ${startTxt} → ${endTxt}${draft.wrap?' (wrap)':''} · ${durStr}`;
      t.style.background = dark ? 'rgba(30,38,50,0.92)' : 'rgba(235,243,255,0.95)';
      t.style.borderColor = dark ? '#35507b' : '#95b8eb';
    }
  };

  useEffect(() => {
    const move = (e) => handlePointerMove(e);
    const up = () => handlePointerUp();
    if (dragStart) {
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
    }
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [dragStart, dragCurrent]);

  // Drawing
  useEffect(() => {
    const svgEl = svgRef.current; if (!svgEl) return;
    const innerWidth = width - MARGIN.left - MARGIN.right;
  // Total height simplifies because ROW_GAP = 0
  const totalHeight = MARGIN.top + MARGIN.bottom + 7 * TRACK_HEIGHT + 6 * ROW_GAP;
    const svg = d3.select(svgEl).attr('width', width).attr('height', totalHeight).style('background', 'transparent');
    svg.selectAll('*').remove();

    const x = buildScales();

    // Hour label header (0 - 24) above tracks
    const headerG = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top - 8})`);
    for (let h = 0; h <= 24; h++) {
      const xPos = x(h * 60);
      headerG.append('text')
        .attr('x', xPos)
        .attr('y', -4)
        .text(h)
        .attr('fill', dark ? '#b9c2cc' : '#4a5563')
        .style('font-size', '10px')
        .style('font-family', 'Nunito, sans-serif')
        .style('text-anchor', 'middle');
    }

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const draft = computeRange(dragStart, dragCurrent);
    const effective = draft || range;

    // Draw each day row
    for (let day = 0; day < 7; day++) {
  const yOffset = day * (TRACK_HEIGHT + ROW_GAP); // ROW_GAP=0 -> contiguous rows
      const rowG = g.append('g').attr('transform', `translate(0,${yOffset})`);

      // Background track
      rowG.append('rect')
        .attr('x', 0).attr('y', 0)
        .attr('width', innerWidth).attr('height', TRACK_HEIGHT)
        .attr('rx', 4)
        .attr('fill', dark ? '#1f2124' : '#f5f6f8')
        .attr('stroke', dark ? '#444' : '#ccc');

      // Hour lines
      for (let h = 1; h < 24; h++) { // remove 0 and 24 boundary lines
        const xPos = x(h * 60);
        rowG.append('line')
          .attr('x1', xPos).attr('x2', xPos)
          .attr('y1', 0).attr('y2', TRACK_HEIGHT)
          .attr('stroke', dark ? '#333' : '#ddd')
          .attr('stroke-width', 0.55);
      }
      // Quarter lines
      for (let i = 0; i < SLOTS_PER_DAY; i++) {
        const m = i * SLOT; if (m % 60 === 0) continue; const xPos = x(m);
        rowG.append('line')
          .attr('x1', xPos).attr('x2', xPos)
          .attr('y1', 0).attr('y2', TRACK_HEIGHT)
          .attr('stroke', dark ? '#2a2c2f' : '#eee')
          .attr('stroke-width', 0.5);
      }

      // Selection highlight segments for this day
      if (effective) {
        // Determine if day intersects selection
        const dayStartWeekIdx = day * TOTAL_MINUTES_DAY;
        const dayEndWeekIdx = dayStartWeekIdx + TOTAL_MINUTES_DAY; // exclusive
        const selStartIdx = effective.startDay * TOTAL_MINUTES_DAY + effective.startMinute;
        let selEndIdx = effective.endDay * TOTAL_MINUTES_DAY + effective.endMinute; // exclusive absolute index
        const weekTotal = 7 * TOTAL_MINUTES_DAY;
        if (!effective.wrap) {
          const intersectStart = Math.max(selStartIdx, dayStartWeekIdx);
          const intersectEnd = Math.min(selEndIdx, dayEndWeekIdx);
          if (intersectEnd > intersectStart) {
            const localStartMin = intersectStart - dayStartWeekIdx;
            const localEndMin = intersectEnd - dayStartWeekIdx;
            rowG.append('rect')
              .attr('x', x(localStartMin)).attr('y', 2)
              .attr('width', Math.max(4, x(localEndMin) - x(localStartMin)))
              .attr('height', TRACK_HEIGHT - 4)
              .attr('rx', 4)
              .attr('fill', dark ? 'rgba(140,180,255,0.35)' : 'rgba(90,140,255,0.35)')
              .attr('stroke', dark ? '#6da0ff' : '#3d7dff')
              .attr('stroke-width', 1.1);
          }
        } else {
          // wrap: selection spans selStartIdx..weekTotal + 0..selEndIdx
          // First segment (from selStartIdx to week end)
          const firstEnd = weekTotal;
          const intersectStart1 = Math.max(selStartIdx, dayStartWeekIdx);
          const intersectEnd1 = Math.min(firstEnd, dayEndWeekIdx);
          const SecondSegmentStart = 0; const SecondSegmentEnd = selEndIdx; // second segment absolute indices
          if (intersectEnd1 > intersectStart1) {
            const localStartMin = intersectStart1 - dayStartWeekIdx;
            const localEndMin = intersectEnd1 - dayStartWeekIdx;
            rowG.append('rect')
              .attr('x', x(localStartMin)).attr('y', 2)
              .attr('width', Math.max(4, x(localEndMin) - x(localStartMin)))
              .attr('height', TRACK_HEIGHT - 4)
              .attr('rx', 4)
              .attr('fill', dark ? 'rgba(140,180,255,0.35)' : 'rgba(90,140,255,0.35)')
              .attr('stroke', dark ? '#6da0ff' : '#3d7dff')
              .attr('stroke-width', 1.1);
          }
          // Second segment intersects days before endDay
          const intersectStart2 = Math.max(SecondSegmentStart, dayStartWeekIdx);
          const intersectEnd2 = Math.min(SecondSegmentEnd, dayEndWeekIdx);
          if (intersectEnd2 > intersectStart2) {
            const localStartMin2 = intersectStart2 - dayStartWeekIdx;
            const localEndMin2 = intersectEnd2 - dayStartWeekIdx;
            rowG.append('rect')
              .attr('x', x(localStartMin2)).attr('y', 2)
              .attr('width', Math.max(4, x(localEndMin2) - x(localStartMin2)))
              .attr('height', TRACK_HEIGHT - 4)
              .attr('rx', 4)
              .attr('fill', dark ? 'rgba(140,180,255,0.35)' : 'rgba(90,140,255,0.35)')
              .attr('stroke', dark ? '#6da0ff' : '#3d7dff')
              .attr('stroke-width', 1.1);
          }
        }
      }

      // Day label
      rowG.append('text')
        .attr('x', -MARGIN.left + 10)
        .attr('y', TRACK_HEIGHT / 2)
        .text(DAYS[day])
        .attr('fill', dark ? '#e0e6ef' : '#1a2533')
        .style('font-size', '12px')
        .style('font-weight', '600')
        .style('font-family', 'Nunito, sans-serif')
        .attr('dominant-baseline', 'middle');
    }

    // Interaction overlay on top of all rows
    svg.append('rect')
      .attr('x', MARGIN.left).attr('y', MARGIN.top - 18)
      .attr('width', innerWidth)
      .attr('height', 7 * TRACK_HEIGHT + 6 * ROW_GAP + 18)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousedown', handlePointerDown)
      .on('mousemove', (event) => handleHoverMove(event))
      .on('mouseleave', hideTooltip);
  }, [width, range, dragStart, dragCurrent, dark]);

  const clearRange = () => { setRange(null); onRangeChange?.(null); };

  const liveDisplay = () => formatRangeDisplay(computeRange(dragStart, dragCurrent) || range);

  return (
    <div ref={containerRef} className="weekly-stacked-single-range-selector" style={{ width: '100%', zoom: '118%', position:'relative' }}>
      <Text size="sm" fw={600} mb={6}>Weekly Single Range (15-min) – drag across days</Text>
      <svg ref={svgRef} style={{ width: '100%', display: 'block' }} />
      <div style={{ marginTop: 8, display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Text size="xs" style={{ flexGrow: 1 }}>{liveDisplay()}</Text>
        <Button aria-label="weekly-stacked-single-range-clear" size="xs" color="gray" variant="light" onClick={clearRange}>Clear</Button>
      </div>
      <div style={{ marginTop: 4 }}>
        <small style={{ opacity: 0.65 }}>Drag horizontally & vertically to span multiple days. Wrap supported: dragging "backwards" crosses week end. End is exclusive (23:59 shown for 24:00).</small>
      </div>
  <div ref={tooltipRef} style={{ position:'absolute', display:'none', color: dark ? '#eef2f7':'#1f2937', padding:'3px 8px', borderRadius:4, fontSize:11, pointerEvents:'none', whiteSpace:'nowrap', boxShadow: dark? '0 2px 6px rgba(0,0,0,0.55)':'0 2px 6px rgba(0,0,0,0.25)', border:'1px solid', background: dark ? 'rgba(50,55,62,0.9)' : 'rgba(255,255,255,0.95)' }} />
    </div>
  );
};

WeeklyStackedSingleRangeSelector.propTypes = {
  initialRange: PropTypes.shape({
    startDay: PropTypes.number,
    startMinute: PropTypes.number,
    endDay: PropTypes.number,
    endMinute: PropTypes.number,
    wrap: PropTypes.bool,
  }),
  onRangeChange: PropTypes.func,
};

export default WeeklyStackedSingleRangeSelector;
